import { AccessLevelEnum } from '@ftr/distribution-contracts'
import { LocalDate, LocalTime, ZoneId } from '@js-joda/core'
import z from 'zod'

const IdNamePairSchema = z.object({
  id: z.uuid(),
  name: z.string(),
})
export type IdNamePair = z.infer<typeof IdNamePairSchema>

const SerializedCartCourtSystemSchema = IdNamePairSchema.extend({
  timezoneId: z.string(),
})
const CartCourtSystemSchema = SerializedCartCourtSystemSchema.transform(serializedCartCourtSystemMetadataSchema => ({
  ...serializedCartCourtSystemMetadataSchema,
  timezoneId: ZoneId.of(serializedCartCourtSystemMetadataSchema.timezoneId),
}))

export type SerializedCartCourtSystem = z.infer<typeof SerializedCartCourtSystemSchema>
export type CartCourtSystem = z.infer<typeof CartCourtSystemSchema>

const SerializedCartTimeSchema = z.object({
  startTime: z.string(),
  endTime: z.string().nullable(),
})

const CartTimeSchema = SerializedCartTimeSchema.transform(serializedCartTimeSchema => ({
  startTime: LocalTime.parse(serializedCartTimeSchema.startTime),
  endTime: serializedCartTimeSchema.endTime ? LocalTime.parse(serializedCartTimeSchema.endTime) : null,
}))

export type CartTime = z.infer<typeof CartTimeSchema>

const CartItemRuleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  accessLevel: AccessLevelEnum,
  publicStatement: z.string(),
})

const SerializedCartItemSchema = SerializedCartTimeSchema.extend({
  id: z.uuid(),
  sessionId: z.uuid(),
  courthouse: IdNamePairSchema,
  courtroom: IdNamePairSchema,
  recordingDate: z.string(),
  rule: CartItemRuleSchema,
  requestedTimes: SerializedCartTimeSchema.array(),
})

const CartItemSchema = SerializedCartItemSchema.transform(serializedCartItemSchema => ({
  ...serializedCartItemSchema,
  recordingDate: LocalDate.parse(serializedCartItemSchema.recordingDate),
  startTime: LocalTime.parse(serializedCartItemSchema.startTime),
  endTime: serializedCartItemSchema.endTime ? LocalTime.parse(serializedCartItemSchema.endTime) : null,
  requestedTimes: serializedCartItemSchema.requestedTimes.map(times => CartTimeSchema.parse(times)),
}))

export type SerializedCartItem = z.infer<typeof SerializedCartItemSchema>
export type CartItem = z.infer<typeof CartItemSchema>

const SerializedCartStateModelSchema = z.object({
  courtSystem: SerializedCartCourtSystemSchema.optional(),
  cartItems: SerializedCartItemSchema.array(),
})

export const CartStateModelSchema = SerializedCartStateModelSchema.transform(serializedCartStateSchema => ({
  courtSystem: serializedCartStateSchema.courtSystem
    ? CartCourtSystemSchema.parse(serializedCartStateSchema.courtSystem)
    : undefined,
  cartItems: serializedCartStateSchema.cartItems.map(item => CartItemSchema.parse(item)),
}))

export type SerializedCartStateModel = z.infer<typeof SerializedCartStateModelSchema>
export type CartStateModel = z.infer<typeof CartStateModelSchema>

export interface CartGroup {
  key: string
  courthouse: IdNamePair
  courtroom: IdNamePair
  recordingDate: LocalDate
  items: CartItem[]
}

export const CART_STATE_DEFAULT: CartStateModel = { courtSystem: undefined, cartItems: [] }
